import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    workPhone: state.components.contactUsForm.fieldValues["workPhone"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface WorkPhoneStyles {
    workPhoneTextField?: TextFieldPresentationProps;
}

export const workPhoneStyles: WorkPhoneStyles = {};

const styles = workPhoneStyles;
const phoneRegex = /^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/;

const WorkPhone: React.FC<Props> = ({ fields, workPhone, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [workPhoneErrorMessage, setWorkPhoneErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.workPhone = validateWorkPhone;
    }, [isRequired,workPhone]);

    const validateWorkPhone = () => {
        const errorMessage =
            !workPhone && isRequired
                ? (siteMessage("Field_Required", label) as string)
                : !workPhone || phoneRegex.test(workPhone)
                ? ""
                : siteMessage("ContactUsForm_PhoneNumberIsInvalidErrorMessage");
        setWorkPhoneErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    const workPhoneChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "workPhone", value: event.target.value });
    };

    const workPhoneBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateWorkPhone();
    };

    return (
        <TextField
            {...styles.workPhoneTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={workPhone || ""}
            onChange={workPhoneChangeHandler}
            onBlur={workPhoneBlurHandler}
            error={workPhoneErrorMessage}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(WorkPhone),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Phone Number",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "(###) ###-####",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
